// The agent chat's left panel: a tab strip over a body.
//
// It replaces the fixed-width <ChatHistoryDrawer> on the running-session page.
// History is the one built-in tab and keeps the drawer's behaviour unchanged
// (it renders the very same <ChatHistoryContent>). Everything else is supplied
// by the host through AgentChatConfig.sidePanelTabs, so a product can add a
// table-of-contents tab, a chart tab, and so on without agentkit learning
// anything about that product.
//
// Two deliberate properties:
//   - The panel renders NO CONTENT when closed: <SlidePanel> unmounts the body
//     once the exit animation has run, so a closed tab never fetches. Open
//     state lives on the host page and is not persisted anywhere; the host
//     decides what a load starts with (Platinum's session page opens it, its
//     start screen does not). When closed it is opened from
//     <SidePanelLauncher>, the button in the chat's top-left corner whose dial
//     names every tab and opens the panel on the one picked (#1795 follow-up:
//     the edge pill, halfway down the left edge, went unfound), or from that
//     <EdgeReveal> pill, which stays beside it and reopens the last tab.
//   - History is the first tab, as a word, but the first HOST tab is the
//     default, so an opened panel shows the host's own content rather than a
//     session list.
//   - The active tab is the panel's own unless the host passes `activeKey`.
//     A host that opens the panel on a chosen tab (the launcher's dial) holds
//     the key and takes changes back through `onActiveKeyChange`.

import React, { useState } from 'react'
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { ChatHistoryContent, type ChatHistoryContentProps } from './ChatHistoryDrawer.js'
import SlidePanel from './SlidePanel.js'
import { HAIRLINE, HAIRLINE_COLOR, INNER_RADIUS } from './surface.js'
import type { AgentSidePanelTab } from '../plugins.js'

/** The built-in History tab's key, for a host that opens the panel on it. */
export const SIDE_PANEL_HISTORY_KEY = '__history'
const HISTORY_TAB_KEY = SIDE_PANEL_HISTORY_KEY

/** The tab an opened panel shows by default: the first host tab, else History. */
export function sidePanelDefaultKey(tabs: AgentSidePanelTab[] = []): string {
  return tabs[0]?.key ?? SIDE_PANEL_HISTORY_KEY
}

export interface AgentSidePanelProps extends ChatHistoryContentProps {
  open: boolean
  onClose: () => void
  /** Host tabs, rendered after History. The first is the default tab. */
  tabs?: AgentSidePanelTab[]
  /** Panel width in px. Defaults to {@link SIDE_PANEL_WIDTH}. */
  width?: number
  /** The active tab, when the host holds it (to open the panel on a chosen
   *  tab). Absent, the panel keeps its own, starting at the default tab. An
   *  unknown key shows History. */
  activeKey?: string
  /** Called when the user picks a tab in the strip. */
  onActiveKeyChange?: (key: string) => void
}

/** Adam, 13 Oct: 420px, fixed. No variable or resizable width. Six text tabs,
 *  History among them, fit the strip without scroll arrows. */
export const SIDE_PANEL_WIDTH = 420

// The tab strip draws real tabs (Matt, 13 Oct: "make it obvious"). The strip is
// a faintly tinted band; an unselected tab is plain text; the selected tab is a
// top-rounded hairline box in the panel's own background that overlaps the
// strip's bottom edge, so it reads as joined to the body below it.
const TAB_STRIP_SX = {
  flex: 1,
  minHeight: 36,
  px: 0.5,
  pt: 0.5,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    flex: '1 1 auto',
    minHeight: 32,
    minWidth: 0,
    px: 1,
    py: 0,
    fontSize: 12,
    textTransform: 'none',
    color: 'text.secondary',
    border: '1px solid transparent',
    borderBottom: 'none',
    borderRadius: `${INNER_RADIUS} ${INNER_RADIUS} 0 0`,
    '&:hover': { color: 'text.primary' },
    '&.Mui-selected': {
      color: 'text.primary',
      fontWeight: 600,
      backgroundColor: 'background.paper',
      borderColor: HAIRLINE_COLOR,
      // Cover the strip's bottom hairline so the tab opens into the body.
      marginBottom: '-1px',
      paddingBottom: '1px',
    },
  },
} as const

export default function AgentSidePanel({
  open,
  onClose,
  tabs = [],
  width = SIDE_PANEL_WIDTH,
  activeKey: hostKey,
  onActiveKeyChange,
  ...history
}: AgentSidePanelProps) {
  // The first host tab is the default; History only when the host supplied none.
  // Held here so the choice survives a close/re-open within one page session —
  // "shows TOC unless the user picked History" — while a fresh page load, which
  // remounts the panel, is back to the default. A host-held key wins.
  const [ownKey, setOwnKey] = useState(() => sidePanelDefaultKey(tabs))
  const activeKey = hostKey ?? ownKey
  const handleTabChange = (key: string) => {
    setOwnKey(key)
    onActiveKeyChange?.(key)
  }

  const activeHostTab = tabs.find(t => t.key === activeKey)
  const showHistory = !activeHostTab

  return (
    <SlidePanel open={open} width={width} side="left">
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderRight: '1px solid rgba(0,0,0,0.06)',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: HAIRLINE, backgroundColor: 'rgba(38,55,87,0.04)' }}>
          <Tabs
            value={showHistory ? HISTORY_TAB_KEY : activeKey}
            onChange={(_e, key: string) => handleTabChange(key)}
            // No scroll arrows: at 420px the six tabs fit, and a scrollable
            // strip keeps room for its arrows even when they are hidden, which
            // pushed Skills off the end (Adam, 13 Oct). The tabs share the width.
            variant="standard"
            sx={TAB_STRIP_SX}
          >
            <Tab value={HISTORY_TAB_KEY} label="History" />
            {tabs.map(t => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>
          <Tooltip title="Close panel">
            <IconButton size="small" aria-label="Close panel" onClick={onClose} sx={{ mr: 0.5, mb: 0.25 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {showHistory ? <ChatHistoryContent {...history} /> : activeHostTab!.render()}
        </Box>
      </Box>
    </SlidePanel>
  )
}
